import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DashboardWidgetType } from '../../../../../models/big-screen-dashboard.model';

/** One choosable widget type with its display copy. */
interface WidgetTypeOption {
  type: DashboardWidgetType;
  label: string;
  description: string;
}

/**
 * Modal picker listing the widget types a user can add to a dashboard. Purely
 * presentational: it emits the chosen type and lets the parent create, position,
 * and persist the widget.
 */
@Component({
  selector: 'app-add-widget',
  standalone: false,
  templateUrl: './add-widget.component.html',
  // shared.scss first so this component's own rules (e.g. the .modal z-index
  // override that lifts the picker above the big-screen overlay) win the cascade.
  styleUrls: ['../../../styles/shared.scss', './add-widget.component.scss'],
})
export class AddWidgetComponent {
  @Input() open: boolean = false;
  @Input() dark: boolean = false;

  @Output() add = new EventEmitter<DashboardWidgetType>();
  @Output() cancel = new EventEmitter<void>();

  /** The type list, held alphabetically by label so the picker reads A–Z. */
  private readonly options: WidgetTypeOption[] = [
    // Temporarily hidden from the picker while the errors-by-result-code widget is
    // being fixed. Re-add to restore it; the widget type itself still works.
    // { type: 'errorsByResultCode', label: 'Errors by Result Code', description: 'Per-result-code error breakdown.' },
    { type: 'image', label: 'Image', description: 'A standalone uploaded image, such as a logo or banner.' },
    { type: 'multiLineGraph', label: 'Multi-line Graph', description: 'Attendance over time, one line per entrance.' },
    { type: 'scanRateByEntrance', label: 'Scan Rate by Entrance', description: 'Per-entrance grid; respects entrance groups.' },
    { type: 'entranceGraph', label: 'Single Entrance Graph', description: 'Attendance over time for one entrance.' },
    { type: 'statTile', label: 'Stat Tile', description: 'A single headline number (entries, fans inside, scan rate…).' },
    { type: 'textNote', label: 'Text', description: 'Free-form text to label or annotate the dashboard.' },
    { type: 'venueMap', label: 'Venue Map', description: 'A floor-plan image with entrance markers showing entries, exits and re-entries.' },
  ];

  /** The current search text; filters the option list by label and description. */
  query: string = '';

  /** Options matching the search box, or all options when the box is empty. */
  get filteredOptions(): WidgetTypeOption[] {
    const term = this.query.trim().toLowerCase();
    if (!term) return this.options;
    return this.options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(term) ||
        opt.description.toLowerCase().includes(term)
    );
  }

  choose(type: DashboardWidgetType): void {
    this.query = '';
    this.add.emit(type);
  }

  close(): void {
    this.query = '';
    this.cancel.emit();
  }
}
